import {ProjectGeneralInformation} from '../../../../app/pages/fleet-team/one-list/interfaces/project-general-information.interface';
import {ProjectSupportDocumentation} from '../../../../app/pages/fleet-team/one-list/interfaces/project-support-documentation.interface';
import {ProjectTimeline} from '../../../../app/pages/fleet-team/one-list/interfaces/project-timeline.interface';
import {ProjectRequirements} from '../../../../app/pages/fleet-team/one-list/interfaces/project-requirements.interface';
import {ProjectCost} from '../../../../app/pages/fleet-team/one-list/interfaces/project-cost.interface';
import {ProjectMisc} from '../../../../app/pages/fleet-team/one-list/interfaces/project-misc.interface';

export class FleetTeamOneList {
  id: number;
  modFleet: string;
  effectiveFleets: string;
  modFleetCode: string;
  eprsNumber: string;
  epraNumber: string;
  eoNumber: string;
  poshRevisionIndicator: boolean;
  eaIndicator: boolean;
  additionalNumbers: string;
  title: string;
  vpSponsor: string;
  status: string;
  statusUpdates: string;
  ata: string;
  justification: string;
  reliabilityFormReceived: boolean;
  ad: boolean;
  warranty: boolean;
  fleetTeamAcceptance: Date;
  coreTeamDate: Date;
  engineer: string;
  initialSra: string;
  mitigatedSra: string;
  projectCost: number;
  laborCost: number;
  materielCost: number;
  otherAssocCost: number;
  initialEpraPlannedCompletionTimeframe: string;
  currentEpraPlannedCompletionTimeframe: string;
  reliabilityFormReceivedDate: Date;
  scheduling: string;
  aecCount: number;
  aec: string;
  vatApprovalDate: Date;
  mdApprovalDate: Date;
  vatRequired: boolean;
  eoPrelimDate: Date;
  eoReleaseDate: Date;
  enteredBy: string;
  enteredAt: Date;
  updatedBy: string;
  updatedAt: Date;
  capitalOrExpense: string;
  clearNumber: string;
  fleetTeamLeader: string;
  materielRepresentative: string;
  maintenancePlanningRepresentative: string;
  financeRepresentative: string;
  lineMaintenanceRepresentative: string;
  hangarMaintenanceRepresentative: string;
  vendorMaintenanceRepresentative: string;
  flightRepresentative: string;
  safetyRepresentative: string;
  fleetTeamApprovalDate: Date;
  isValid: boolean;
  eoCompletionDate: Date;
  attachments: Array<{ name: string, id: string, fileName: string, originalFileName?: string }>;

  private static titlecase(str: string): string {
    if (!!str) {
      return str.split(' ')
        .map(w => w[0].toUpperCase() + w.substr(1).toLowerCase())
        .join(' ');
    }

    return str;
  }

  getGeneralInformation(): ProjectGeneralInformation {
    return {
      epraNumber: this.epraNumber,
      status: this.status,
      title: this.title,
      ata: this.ata,
      modFleet: this.modFleet,
      effectiveFleets: this.effectiveFleets,
      justification: this.justification,
      vpSponsor: FleetTeamOneList.titlecase(this.vpSponsor),
      initialSra: this.initialSra,
      mitigatedSra: this.mitigatedSra,
      aecCount: this.aecCount,
      aec: this.aec,
      scheduling: this.scheduling,
      ad: this.ad,
      warranty: this.warranty
    };
  }

  getSupportingDocumentation(): ProjectSupportDocumentation {
    return {
      eprsNumber: this.eprsNumber,
      eoNumber: this.eoNumber,
      eoReleaseDate: this.eoReleaseDate,
      additionalNumbers: this.additionalNumbers,
      reliabilityForm: this.getReliabilityForm()
    };
  }

  getTimeline(): ProjectTimeline {
    const reliabilityFormRequired = !!this.justification && this.justification.toLowerCase().includes('reliability');

    return {
      fleetTeamAcceptance: this.fleetTeamAcceptance,
      coreTeamDate: this.coreTeamDate,
      vatApprovalDate: this.vatApprovalDate,
      mdApprovalDate: this.mdApprovalDate,
      eoPrelimDate: this.eoPrelimDate,
      eoReleaseDate: this.eoReleaseDate,
      initialEpraPlannedCompletionTimeframe: this.initialEpraPlannedCompletionTimeframe,
      currentEpraPlannedCompletionTimeframe: this.currentEpraPlannedCompletionTimeframe,
      fleetTeamApprovalDate: this.fleetTeamApprovalDate,
      displayVatMdApprovalDate: this.vatRequired || !!this.vatApprovalDate,
      reliabilityFormReceivedDate: this.reliabilityFormReceivedDate,
      reliabilityFormRequired,
      eoCompletionDate: this.eoCompletionDate
    };
  }

  getRequirements(): ProjectRequirements {
    const reliabilityFormRequired = !!this.justification && this.justification.toLowerCase().includes('reliability');

    return {
      reliabilityFormReceived: this.reliabilityFormReceived,
      poshRevisionIndicator: this.poshRevisionIndicator,
      vatRequired: this.vatRequired,
      reliabilityFormRequired
    };
  }

  getCost(): ProjectCost {
    return {
      capitalOrExpense: this.capitalOrExpense,
      clearNumber: this.clearNumber,
      projectCost: this.projectCost,
      laborCost: this.laborCost,
      materielCost: this.materielCost,
      otherAssocCost: this.otherAssocCost
    };
  }

  getMisc(): ProjectMisc {
    return {
      enteredBy: this.enteredBy,
      enteredAt: this.enteredAt,
      updatedBy: this.updatedBy,
      updatedAt: this.updatedAt,
      statusUpdates: this.statusUpdates
    };
  }

  addReliabilityForm(documentID: string, fileName: string, originalFileName: string) {
    if (!this.attachments) {
      this.attachments = [];
    }

    const existingForm = this.attachments.find(a => a.name === 'reliabilityForm');


    if (!!existingForm) {
      existingForm.id = documentID;
    } else {
      this.attachments.push({name: 'reliabilityForm', id: documentID, fileName, originalFileName});
    }

    this.reliabilityFormReceived = true;
    this.reliabilityFormReceivedDate = new Date();
    this.setAttachments(this.attachments);
  }

  hasReliabilityForm(): boolean {
    if (!this.attachments) {
      this.attachments = [];
      return false;
    }

    return !!this.attachments.find(a => a.name === 'reliabilityForm');
  }

  removeReliabilityForm() {
    if (!this.attachments) {
      this.attachments = [];
    }

    const attachments = this.attachments.filter(a => a.name !== 'reliabilityForm');
    this.reliabilityFormReceivedDate = null;
    this.reliabilityFormReceived = false;
    this.setAttachments(attachments);
  }

  getReliabilityForm(): { name: string; id: string; fileName: string } {
    if (!this.attachments) {
      this.attachments = [];
      return null;
    }

    return this.attachments.find(a => a.name === 'reliabilityForm');
  }

  setAttachments(attachments: { name: string; id: string; fileName: string, originalFileName?: string }[]) {
    if (!!attachments) {
      this.attachments = attachments;
    }
  }
}
